import type { Placement } from "@floating-ui/react";
import React, { useRef, useEffect, useState, lazy, Suspense } from "react";
import { cx } from "../../utils";

const Tooltip = lazy(() =>
  import("../Tooltip").then((module) => ({ default: module.Tooltip }))
);
const TooltipContent = lazy(() =>
  import("../Tooltip").then((module) => ({ default: module.TooltipContent }))
);
const TooltipTrigger = lazy(() =>
  import("../Tooltip").then((module) => ({ default: module.TooltipTrigger }))
);
interface TruncateWithTooltip {
  content: React.ReactNode;
  maxWidth?: `${number}px` | number;
  className?: string;
  tooltipClassName?: string;
  placement?: Placement;
}

export const TruncateWithTooltip: React.FC<TruncateWithTooltip> = ({
  content,
  maxWidth,
  className = "",
  tooltipClassName = "",
  placement
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (contentRef.current) {
        const element = contentRef.current;
        setIsTruncated(element.scrollWidth > element.clientWidth);
      }
    };

    checkTruncation();

    // Re-check on window resize
    const handleResize = () => {
      checkTruncation();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [content]);

  return (
    <Suspense
      fallback={
        <div
          ref={contentRef}
          className={cx("truncate", className)}
          style={maxWidth ? { maxWidth } : undefined}
        >
          {content}
        </div>
      }
    >
      <Tooltip placement={placement}>
        <TooltipTrigger asChild>
          <div
            ref={contentRef}
            className={cx("truncate", className)}
            style={maxWidth ? { maxWidth } : undefined}
          >
            {content}
          </div>
        </TooltipTrigger>
        {isTruncated ? (
          <TooltipContent
            className={cx(
              "max-w-[300px] rounded-lg border p-4 text-sm text-white",
              tooltipClassName
            )}
          >
            {content}
          </TooltipContent>
        ) : null}
      </Tooltip>
    </Suspense>
  );
};
